import type { EngineInput, EngineResult, ProfitLeak } from './estimate.types';
import {
  DEFAULT_ESTIMATE_RATES,
  MINIMUM_VISIT_PRICING_NOTE,
  SMALL_OPENINGS_PRICING_NOTE,
  resolveEstimateRate,
  resolveRegionPricingConfig,
  type EstimateRateType,
  type EstimateRates,
} from './estimate.rates';
import { getLeakMessage } from './leaks/leak.messages';
import { parseWorkScope } from './scopeParser';
import { createEstimateItem } from './estimateItemFactory';
import { suggestMaterials } from './materialSuggestions';
import {
  calculateMaterialModeSummary,
  type MaterialMode as DomainMaterialMode,
  type MaterialClientLine,
} from './materialMode';
import {
  calculatePercentageMoney,
  multiplyMoney,
  roundMoney,
  sumMoney,
} from './money';
import { calculateQuickRoomSurface } from './surface/openingsCalculator';

export const DOOR_AREA = 2;
export const DEFAULT_BASE_RATE = 25;
export const APARTMENT_WORKING_AREA_FACTOR = 2.6;
export const APARTMENT_BASE_HEIGHT = 2.6;
export const APARTMENT_CEILING_FACTOR = 1;
export const APARTMENT_WALL_FACTOR_AT_260 = 1.6;

export const DEFAULT_RATES = {
  primer: DEFAULT_ESTIMATE_RATES.primer,
  hydro: DEFAULT_ESTIMATE_RATES.hydro,
  painting: DEFAULT_ESTIMATE_RATES.painting,
  plaster: DEFAULT_ESTIMATE_RATES.plaster,
  sanding: DEFAULT_ESTIMATE_RATES.sanding,
} as const;

export type RoomType = 'bathroom' | 'room';
export type ProfitLeakType = ProfitLeak['type'];
export type UserRates = EstimateRates;
export type LaborRatePreset = 'tanio' | 'standard' | 'premium';
export type MaterialMode = DomainMaterialMode;
export type SettlementMode = 'no_vat' | 'vat_8' | 'vat_23';

export interface ClientPricingSummary {
  workNet: number;
  clientVisibleMaterialTotal: number;
  netTotal: number;
  vatRate: number;
  vatAmount: number;
  grossTotal: number;
}

export type ClientMaterialLine = MaterialClientLine;

export type EstimateInput = EngineInput & {
  roomType?: RoomType;
  userRates?: UserRates;
};

function roundArea(value: number): number {
  return Math.round(value * 10) / 10;
}

function formatNumber(value: number): string {
  const rounded = Math.round(value * 100) / 100;
  return Number.isInteger(rounded) ? rounded.toFixed(0) : String(rounded);
}

function formatArea(value: number): string {
  return `${formatNumber(value)} m²`;
}

function formatRate(value: number, unit: string): string {
  return `${formatNumber(value)} zł/${unit}`;
}

function getRate(type: EstimateRateType, userRates?: UserRates, region?: string): number {
  return resolveEstimateRate(type, region, userRates);
}

function getApartmentEffectiveHeight(height: number): number {
  return height > 0 ? height : APARTMENT_BASE_HEIGHT;
}

export function calculateApartmentWorkingAreaFactor(height: number): number {
  const effectiveHeight = getApartmentEffectiveHeight(height);
  return APARTMENT_CEILING_FACTOR + APARTMENT_WALL_FACTOR_AT_260 * (effectiveHeight / APARTMENT_BASE_HEIGHT);
}

export function calculatePerimeter(length: number, width: number): number {
  return 2 * (length + width);
}

export function calculateWallArea(perimeter: number, height: number): number {
  return perimeter * height;
}

export function calculateCeilingArea(length: number, width: number): number {
  return length * width;
}

export function calculateDoorArea(doorCount: number): number {
  return doorCount * DOOR_AREA;
}

export function calculateOpeningsArea(openingsArea: number): number {
  return Math.max(0, openingsArea);
}

export function calculateEffectiveWallArea(
  wallArea: number,
  doorArea: number,
  openingsArea: number
): number {
  return Math.max(0, wallArea - doorArea - openingsArea);
}

export function calculateCost(area: number, rate: number): number {
  return multiplyMoney(area, rate);
}

export function getLaborRateMultiplier(preset: LaborRatePreset): number {
  if (preset === 'tanio') return 0.9;
  if (preset === 'premium') return 1.25;
  return 1;
}

export function applyLaborRateMultiplier(
  items: EngineResult['items'],
  previousMultiplier: number,
  nextMultiplier: number
): EngineResult['items'] {
  const safePreviousMultiplier = previousMultiplier || 1;

  return items.map((item) => {
    const baseRate = item.unitRateNetto / safePreviousMultiplier;
    const nextRate = roundMoney(baseRate * nextMultiplier);
    const nextTotal = multiplyMoney(item.quantity, nextRate);

    return {
      ...item,
      unitPrice: nextRate,
      unitRateNetto: nextRate,
      total: nextTotal,
      totalNetto: nextTotal,
    };
  });
}

export function calculateClientPricingSummary(input: {
  items: EngineResult['items'];
  materialSuggestions?: EngineResult['meta']['materialSuggestions'];
  materialMode: MaterialMode;
  materialMarkupPercent?: number;
  settlementMode: SettlementMode;
}): ClientPricingSummary {
  const workNet = sumMoney(input.items.map((item) => item.totalNetto));
  const clientVisibleMaterialTotal = calculateMaterialModeSummary({
    materials: input.materialSuggestions,
    mode: input.materialMode,
    markupPercent: input.materialMarkupPercent,
  }).clientMaterialTotal;
  const netTotal = sumMoney([workNet, clientVisibleMaterialTotal]);
  const vatRate = getSettlementVatRate(input.settlementMode);
  const vatAmount = calculatePercentageMoney(netTotal, vatRate);
  const grossTotal = sumMoney([netTotal, vatAmount]);

  return {
    workNet,
    clientVisibleMaterialTotal,
    netTotal,
    vatRate,
    vatAmount,
    grossTotal,
  };
}

export function buildClientMaterialLines(input: {
  materialSuggestions?: EngineResult['meta']['materialSuggestions'];
  materialMode: MaterialMode;
  materialMarkupPercent?: number;
}): ClientMaterialLine[] {
  return calculateMaterialModeSummary({
    materials: input.materialSuggestions,
    mode: input.materialMode,
    markupPercent: input.materialMarkupPercent,
  }).clientLines;
}

export function getSettlementVatRate(mode: SettlementMode): number {
  if (mode === 'vat_8') return 0.08;
  if (mode === 'vat_23') return 0.23;
  return 0;
}

function createWorkItem(data: {
  id: string;
  label: string;
  quantity: number;
  unitRate: number;
  category: string;
}) {
  return createEstimateItem({
    id: data.id,
    label: data.label,
    category: data.category,
    quantity: data.quantity,
    unit: 'm²',
    unitPrice: data.unitRate,
    pricingMode: 'area',
  });
}

function appendCalculationNote(...notes: Array<string | undefined>): string | undefined {
  const cleanNotes = notes.filter((note): note is string => Boolean(note));
  return cleanNotes.length > 0 ? cleanNotes.join(' ') : undefined;
}

function applyMinimumLaborProtection(
  items: EngineResult['items'],
  region?: string
): { items: EngineResult['items']; note?: string } {
  const workNet = sumMoney(items.map((item) => item.totalNetto));
  const config = resolveRegionPricingConfig(region);
  const isStandaloneLabor = items.length === 1;

  if (!isStandaloneLabor || workNet <= 0 || workNet >= config.minimumVisitNet) {
    return { items };
  }

  const adjustment = roundMoney(config.minimumVisitNet - workNet);
  const protectedItems: EngineResult['items'] = [
    ...items,
    createEstimateItem({
      id: 'minimum_visit_startup',
      label: 'Minimalny koszt wejścia na robotę',
      category: 'startup',
      quantity: 1,
      unit: 'komplet',
      unitPrice: adjustment,
      pricingMode: 'flat',
      calculation: `${formatNumber(workNet)} zł robocizny + ${formatNumber(adjustment)} zł minimum wejścia = ${formatNumber(config.minimumVisitNet)} zł`,
      order: 900,
    }),
  ];

  return {
    items: protectedItems,
    note: `${MINIMUM_VISIT_PRICING_NOTE} Zastosowano minimum: ${formatNumber(config.minimumVisitNet)} zł netto (${config.label}).`,
  };
}

export function detectLeaks(data: {
  wallArea: number;
  floorArea: number;
  workingArea?: number;
  hasPainting: boolean;
  hasPrimer: boolean;
  hasHydro: boolean;
  roomType: RoomType;
  userRates?: UserRates;
  region?: string;
}): ProfitLeak[] {
  const leaks: ProfitLeak[] = [];

  if (data.hasPainting && !data.hasPrimer) {
    const type: ProfitLeakType = 'primer';
    const unitRate = getRate(type, data.userRates, data.region);
    const quantity = data.workingArea ?? data.wallArea;
    const value = multiplyMoney(quantity, unitRate);
    const unit = 'm²';
    const calculation = `${formatArea(quantity)} × ${formatRate(unitRate, unit)} = ${formatNumber(value)} zł`;

    leaks.push({
      id: 'primer',
      type,
      severity: 'critical',
      label: 'Brak gruntowania',
      message: getLeakMessage({
        id: 'primer',
        type,
        severity: 'critical',
        label: 'Brak gruntowania',
        message: '',
        value,
        quantity,
        unit,
        unitRate,
        calculation,
        aiAdded: false,
        dismissed: false,
      }),
      value,
      quantity,
      unit,
      unitRate,
      calculation,
      aiAdded: false,
      dismissed: false,
      decisionStatus: 'recommended',
      suggestedItem: {
        id: 'primer',
        label: 'Gruntowanie',
        quantity,
        unit,
        unitPrice: unitRate,
        total: value,
        pricingMode: 'area',
        unitRateNetto: unitRate,
        totalNetto: value,
        calculation,
        category: 'grunt',
      },
    });
  }

  if (data.roomType === 'bathroom' && !data.hasHydro) {
    const type: ProfitLeakType = 'hydro';
    const unitRate = getRate(type, data.userRates, data.region);
    const quantity = data.floorArea * 1.5;
    const value = multiplyMoney(quantity, unitRate);
    const unit = 'm²';
    const calculation = `${formatArea(quantity)} × ${formatRate(unitRate, unit)} = ${formatNumber(value)} zł`;

    leaks.push({
      id: 'hydro',
      type,
      severity: 'critical',
      label: 'Brak hydroizolacji',
      message: getLeakMessage({
        id: 'hydro',
        type,
        severity: 'critical',
        label: 'Brak hydroizolacji',
        message: '',
        value,
        quantity,
        unit,
        unitRate,
        calculation,
        aiAdded: false,
        dismissed: false,
      }),
      value,
      quantity,
      unit,
      unitRate,
      calculation,
      aiAdded: false,
      dismissed: false,
      decisionStatus: 'recommended',
      suggestedItem: {
        id: 'hydro',
        label: 'Hydroizolacja',
        quantity,
        unit,
        unitPrice: unitRate,
        total: value,
        pricingMode: 'area',
        unitRateNetto: unitRate,
        totalNetto: value,
        calculation,
        category: 'hydro',
      },
    });
  }

  return leaks;
}

export function calculateEstimate(data: EstimateInput): EngineResult {
  const edgesOnly = data.tradeEngine === 'paving' && data.pavingAreaM2 === 0 && (data.edgeLengthM ?? 0) > 0;
  const scopeText = data.scopeText ?? '';
  const hasScopeText = scopeText.trim().length > 0;
  const parsedScope = parseWorkScope(scopeText);
  const projectMode = data.projectMode ?? (parsedScope.isApartment ? 'apartment' : 'room');
  const length = data.length ?? 0;
  const width = data.width ?? 0;
  const inputHeight = data.height ?? 0;
  const floorAreaInput = data.floorArea ?? 0;
  const pavingWorkingAreaM2 =
    data.tradeEngine === 'paving' && data.pavingAreaM2 && data.pavingAreaM2 > 0
      ? roundArea(data.pavingAreaM2)
      : undefined;
  const manualWorkingAreaM2 = data.manualWorkingAreaM2 && data.manualWorkingAreaM2 > 0
    ? roundArea(data.manualWorkingAreaM2)
    : pavingWorkingAreaM2;
  const hasRoomDimensions = length > 0 && width > 0;
  const isApartmentFactor = projectMode === 'apartment' && floorAreaInput > 0 && !hasRoomDimensions;
  const apartmentEffectiveHeight = getApartmentEffectiveHeight(inputHeight);
  const apartmentWorkingAreaFactor = calculateApartmentWorkingAreaFactor(inputHeight);
  const height = isApartmentFactor ? apartmentEffectiveHeight : inputHeight;
  const includeCeiling = data.includeCeiling ?? true;
  const roomSurface = hasRoomDimensions
    ? calculateQuickRoomSurface({
        lengthM: length,
        widthM: width,
        heightM: height,
        doorCount: data.doorCount ?? 0,
        largeOpeningsAreaM2: data.bigWindowsArea ?? data.openingsArea ?? 0,
        includeCeiling,
      })
    : null;

  const perimeter = roomSurface?.perimeterM ?? (hasRoomDimensions
    ? calculatePerimeter(length, width)
    : data.perimeter ?? 0);
  const floorArea = roomSurface?.floorAreaM2 ?? (isApartmentFactor
    ? floorAreaInput
    : hasRoomDimensions
      ? calculateCeilingArea(length, width)
      : floorAreaInput);
  const ceilingArea = roomSurface?.ceilingAreaM2 ?? (isApartmentFactor
    ? floorArea
    : hasRoomDimensions
      ? (includeCeiling ? floorArea : 0)
      : (includeCeiling ? floorArea : 0));
  const wallArea = roomSurface?.wallGrossAreaM2 ?? (isApartmentFactor
    ? roundArea(Math.max(0, floorAreaInput * (apartmentWorkingAreaFactor - APARTMENT_CEILING_FACTOR)))
    : roundArea(calculateWallArea(perimeter, height)));
  const doorArea = roomSurface?.doorDeductionM2 ?? calculateDoorArea(data.doorCount ?? 0);
  const openingsArea = roomSurface?.largeOpeningsDeductionM2 ?? calculateOpeningsArea(data.bigWindowsArea ?? data.openingsArea ?? 0);
  const effectiveArea = roomSurface?.wallNetAreaM2 ?? roundArea(calculateEffectiveWallArea(wallArea, doorArea, openingsArea));
  const totalWorkingArea = manualWorkingAreaM2 ?? roomSurface?.finalWorkingAreaM2 ?? (isApartmentFactor
    ? roundArea(floorAreaInput * apartmentWorkingAreaFactor)
    : roundArea(effectiveArea + ceilingArea));
  const work = {
    painting: data.work?.painting ?? (parsedScope.hasPainting || data.hasPainting || data.tradeEngine === 'painting' || data.trade === 'malarz'),
    plaster: data.work?.plaster ?? (hasScopeText ? parsedScope.hasPlaster : false),
    primer: data.work?.primer ?? (hasScopeText ? parsedScope.hasPrimer : data.hasPrimer ?? false),
    sanding: data.work?.sanding ?? (hasScopeText ? parsedScope.hasSanding || parsedScope.hasPlaster : false),
    scraping: data.work?.scraping ?? (hasScopeText ? parsedScope.hasScraping : data.hasScraping ?? false),
  };
  const items = [
    ...(edgesOnly
      ? [createEstimateItem({
          id: 'paving_edges', label: 'Obrzeża / krawężniki', category: 'bruk',
          quantity: data.edgeLengthM!, unit: 'mb',
          unitPrice: getRate('paving_edges', data.userRates, data.region), pricingMode: 'linear',
        })]
      : data.tradeEngine === 'paving'
      ? [
          createWorkItem({
            id: 'paving_laying',
            label: 'Układanie kostki',
            quantity: totalWorkingArea,
            unitRate: data.baseRate ?? getRate('paving_laying', data.userRates, data.region),
            category: 'bruk',
          }),
        ]
      : []),
    // Scraping precedes priming in the work sequence (§6: grunt → gładź → szlifowanie → malowanie).
    ...(work.scraping
      ? [
          createWorkItem({
            id: 'scraping',
            label: 'Skrobanie / przygotowanie podłoża',
            quantity: totalWorkingArea,
            unitRate: getRate('scraping', data.userRates, data.region),
            category: 'przygotowanie',
          }),
        ]
      : []),
    ...(work.primer
      ? [
          createWorkItem({
            id: 'primer',
            label: 'Gruntowanie podłoża',
            quantity: totalWorkingArea,
            unitRate: getRate('primer', data.userRates, data.region),
            category: 'grunt',
          }),
        ]
      : []),
    ...(work.plaster
      ? [
          createWorkItem({
            id: 'plaster',
            label: 'Wykonanie gładzi z obróbką i szlifowaniem',
            quantity: totalWorkingArea,
            unitRate: getRate('plaster', data.userRates, data.region),
            category: 'gladz',
          }),
        ]
      : []),
    ...(work.sanding && !work.plaster
      ? [
          createWorkItem({
            id: 'sanding',
            label: 'Szlifowanie gładzi',
            quantity: totalWorkingArea,
            unitRate: getRate('sanding', data.userRates, data.region),
            category: 'szlifowanie',
          }),
        ]
      : []),
    ...(work.painting
      ? [
          createWorkItem({
            id: 'painting_2x',
            label: 'Malowanie 2x',
            quantity: totalWorkingArea,
            unitRate: data.baseRate ?? getRate('painting', data.userRates, data.region),
            category: 'malowanie',
          }),
        ]
      : []),
  ];
  const laborProtection = edgesOnly ? { items, note: undefined } : applyMinimumLaborProtection(items, data.region);
  const protectedItems = laborProtection.items;
  const cost = sumMoney(protectedItems.map((item) => item.totalNetto));
  const hasPrimerItem = items.some((item) => item.category === 'grunt');
  const shouldDetectPrimerLeak = (work.painting || work.plaster) && !work.primer && !hasPrimerItem;
  const leaks = detectLeaks({
    wallArea,
    floorArea,
    workingArea: totalWorkingArea,
    hasPainting: shouldDetectPrimerLeak,
    hasPrimer: !shouldDetectPrimerLeak,
    hasHydro: data.hasHydro ?? true,
    roomType: data.roomType ?? 'room',
    userRates: data.userRates,
    region: data.region,
  });
  const measurementMode = manualWorkingAreaM2
    ? 'manual_working_area'
    : isApartmentFactor
    ? 'apartment_factor_approx'
    : projectMode === 'apartment'
      ? 'apartment_rect_approx'
      : 'room_exact';
  const calculationNote = appendCalculationNote(
    projectMode === 'apartment'
      ? 'Obmiar mieszkania przybliżony. Dla dokładności dodaj pomieszczenia osobno.'
      : undefined,
    SMALL_OPENINGS_PRICING_NOTE,
    laborProtection.note
  );
  const calculations = {
    floorArea: isApartmentFactor
      ? `powierzchnia mieszkania = ${formatArea(floorArea)}`
      : `${formatNumber(length)} × ${formatNumber(width)} = ${formatArea(floorArea)}`,
    perimeter: isApartmentFactor
      ? 'obwód: obmiar przybliżony dla mieszkania'
      : `2 × (${formatNumber(length)} + ${formatNumber(width)}) = ${formatNumber(perimeter)} mb`,
    wallsGross: isApartmentFactor
      ? `${formatArea(floorArea)} × ${formatNumber(APARTMENT_WALL_FACTOR_AT_260)} × (${formatNumber(height)} / ${formatNumber(APARTMENT_BASE_HEIGHT)}) = ${formatArea(wallArea)}`
      : `${formatNumber(perimeter)} × ${formatNumber(height)} = ${formatArea(wallArea)}`,
    doorsDeduction: `${formatNumber(data.doorCount ?? 0)} × ${formatNumber(DOOR_AREA)} = ${formatArea(doorArea)}`,
    bigWindowsDeduction: openingsArea > 0 ? formatArea(openingsArea) : '0 m²',
    wallsNet: `${formatNumber(wallArea)} - ${formatNumber(doorArea)} - ${formatNumber(openingsArea)} = ${formatArea(effectiveArea)}`,
    totalWorkingArea: manualWorkingAreaM2
      ? `powierzchnia do wyceny = ${formatArea(totalWorkingArea)}`
      : isApartmentFactor
      ? `${formatArea(floorArea)} × (1 + ${formatNumber(APARTMENT_WALL_FACTOR_AT_260)} × ${formatNumber(height)} / ${formatNumber(APARTMENT_BASE_HEIGHT)}) = ${formatArea(totalWorkingArea)}`
      : `${formatNumber(effectiveArea)} + ${formatNumber(ceilingArea)} = ${formatArea(totalWorkingArea)}`,
    measurementMode,
    calculationNote,
  };

  const result: EngineResult = {
    items: protectedItems,
    cost,
    leaks,
    meta: {
      perimeter,
      length,
      width,
      height,
      floorArea,
      openingsArea,
      wallArea,
      wallsGross: wallArea,
      doorsDeduction: doorArea,
      bigWindowsDeduction: openingsArea,
      ceilingArea,
      effectiveArea,
      wallsNet: effectiveArea,
      totalWorkingArea,
      calculations,
    },
  };

  return {
    ...result,
    meta: {
      ...result.meta,
      materialSuggestions: suggestMaterials(data, result),
    },
  };
}

export type EstimateResult = EngineResult;
export type { EngineResult, ProfitLeak };
